// ExecRisk.js — "กลุ่มเสี่ยงและการดูแล" (pos 90/99)
//
// คำถามของหน้านี้: เด็กที่ต้องช่วยเหลือมีกี่คน กระจุกที่ไหน และครูดูแลไปถึงขั้นไหน
//
// ตัวเลขทุกตัวเป็นชุดเดียวกับที่ครูประจำชั้นและหัวหน้าระดับเห็นในหน้าติดตาม
// ถ้าไม่ตรงกันเมื่อไหร่ ที่ประชุมจะเถียงเรื่องตัวเลขแทนที่จะคุยเรื่องเด็ก
import React, { useMemo, useState } from "react";
import { Badge, Col, Nav, ProgressBar, Row, Table } from "react-bootstrap";
import ReactApexChart from "react-apexcharts";
import {
  ExecPage, Panel, SectionTitle, StatCard, EmptyState, ScrollTable, StackedBar,
  YearPicker, useExecYear, useExecData,
  fmtNum, fmtPct, toNum, fmtThaiDate, RISK_COLORS, RISK_LABELS,
} from "./ExecShell";

const AXIS_LABELS = {
  attendance: "การมาเรียน",
  academic: "การเรียน",
  behavior: "ความประพฤติ",
  concern: "เรื่องกังวล",
};

const CONCERN_LABELS = {
  learning: "การเรียน",
  health: "สุขภาพ",
  family: "ครอบครัว",
  emotional: "อารมณ์/จิตใจ",
  peer: "เพื่อน",
  other: "อื่นๆ",
};

const LEVEL_ORDER = ["red", "orange", "yellow", "green"];

function LevelLegend() {
  return (
    <div className="d-flex gap-3 flex-wrap fs-xs text-secondary">
      {LEVEL_ORDER.map((level) => (
        <span key={level} className="d-flex align-items-center gap-1">
          <span style={{ width: 10, height: 10, borderRadius: 2, background: RISK_COLORS[level], display: "inline-block" }} />
          {RISK_LABELS[level]}
        </span>
      ))}
    </div>
  );
}

// ตารางระดับชั้น × ระดับความเสี่ยง — เห็นทันทีว่าเสี่ยงกระจุกที่ชั้นไหน
function RiskHeatmap({ rows, labelKey, labelName }) {
  if (!rows || rows.length === 0) return <EmptyState message="ยังไม่มีข้อมูล" compact />;
  const maxAtRisk = Math.max(...rows.map((r) => toNum(r.red) + toNum(r.orange) + toNum(r.yellow)), 1);

  return (
    <ScrollTable>
      <Table className="table-sm mb-0 align-middle">
        <thead>
          <tr>
            <th>{labelName}</th>
            <th className="text-end">นักเรียน</th>
            {LEVEL_ORDER.map((level) => (
              <th key={level} className="text-end" style={{ color: RISK_COLORS[level] }}>{RISK_LABELS[level]}</th>
            ))}
            <th style={{ minWidth: 120 }}>สัดส่วน</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const atRisk = toNum(row.red) + toNum(row.orange) + toNum(row.yellow);
            return (
              <tr key={row[labelKey]}>
                <td className="fw-semibold">{row.c_class || row.ic_class}</td>
                <td className="text-end">{fmtNum(row.total)}</td>
                {LEVEL_ORDER.map((level) => (
                  <td key={level} className="text-end" style={{ fontWeight: level === "red" && toNum(row[level]) > 0 ? 600 : 400 }}>
                    {toNum(row[level]) > 0 ? fmtNum(row[level]) : <span className="text-secondary">—</span>}
                  </td>
                ))}
                <td>
                  <StackedBar
                    segments={LEVEL_ORDER.map((level) => ({ key: level, label: RISK_LABELS[level], value: row[level], color: RISK_COLORS[level] }))}
                  />
                  <div className="fs-xs text-secondary mt-1">
                    ต้องดูแล {fmtNum(atRisk)} คน
                    {atRisk === maxAtRisk && atRisk > 0 && <span className="text-danger ms-1">สูงสุด</span>}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </ScrollTable>
  );
}

// ความคืบหน้าการดูแลรายแกน — ตัวชี้วัดว่าระบบดูแลทำงานจริงไหม ไม่ใช่แค่มีเด็กติดเกณฑ์
function CareProgress({ perAxis, axisLabels }) {
  const axes = Object.keys(perAxis || {});
  if (axes.length === 0) return <EmptyState message="ยังไม่มีข้อมูลการดูแล" compact />;

  return axes.map((axis) => {
    const row = perAxis[axis] || {};
    const percent = toNum(row.completePercent);
    return (
      <div key={axis} className="border rounded p-3 mb-2">
        <div className="d-flex align-items-center justify-content-between mb-2">
          <strong>{axisLabels[axis] || axis}</strong>
          <Badge bg={percent >= 80 ? "success" : percent >= 50 ? "warning" : "danger"}>{fmtPct(row.completePercent)}</Badge>
        </div>
        <ProgressBar style={{ height: 8 }} now={percent} variant={percent >= 80 ? "success" : percent >= 50 ? "warning" : "danger"} />
        <div className="d-flex gap-3 flex-wrap fs-xs text-secondary mt-2">
          <span>ติดเกณฑ์ {fmtNum(row.atRisk)} คน</span>
          <span className="text-success">ทำครบ {fmtNum(row.complete)}</span>
          <span className="text-warning">เริ่มแล้ว {fmtNum(row.started)}</span>
          <span className="text-danger">ยังไม่ครบ {fmtNum(row.pending)}</span>
        </div>
      </div>
    );
  });
}

export default function ExecRisk() {
  const { years, yearId, setYearId } = useExecYear();
  const [side, setSide] = useState("academic");
  const query = useMemo(() => ({ year_id: yearId }), [yearId]);
  const { data, loading, error } = useExecData("/api/executive/stats/risk", query);

  const academic = data?.academic || { total: 0, overall: {}, byAxis: {}, byClass: [] };
  const academicCare = data?.academicCare || { perAxis: {} };
  const concerns = data?.concerns || { available: false, byCategory: [] };
  const islamic = data?.islamic || { available: false };
  const islamicCare = data?.islamicCare || { available: false, perAxis: {} };
  const academicTrend = useMemo(() => (data?.academicTrend || { hasTrend: false, points: [] }), [data]);
  const islamicTrend = useMemo(() => (data?.islamicTrend || { hasTrend: false, points: [] }), [data]);

  const active = side === "islamic" ? islamic : academic;
  const trend = side === "islamic" ? islamicTrend : academicTrend;
  const activeCare = side === "islamic" ? islamicCare : academicCare;
  const axisLabels = side === "islamic"
    ? { attendance: AXIS_LABELS.attendance, behavior: AXIS_LABELS.behavior }
    : AXIS_LABELS;

  const overall = active?.overall || {};

  // แนวโน้มย้อนหลังมาจาก snapshot รายวัน ไม่ใช่การคำนวณย้อนหลัง (ดู jobs/riskSnapshot.js)
  const trendOptions = useMemo(() => ({
    chart: { type: "area", stacked: true, toolbar: { show: false } },
    colors: [RISK_COLORS.red, RISK_COLORS.orange, RISK_COLORS.yellow],
    stroke: { curve: "smooth", width: 2 },
    fill: { type: "gradient", gradient: { shadeIntensity: 1, opacityFrom: 0.5, opacityTo: 0.15 } },
    dataLabels: { enabled: false },
    legend: { position: "top", fontSize: "12px" },
    xaxis: {
      categories: trend.points.map((p) => fmtThaiDate(p.date)),
      labels: { style: { fontSize: "10px" }, rotate: -45, hideOverlappingLabels: true },
    },
    yaxis: { labels: { style: { colors: "#6e7985", fontSize: "11px" } } },
    grid: { borderColor: "rgba(72,94,144,0.08)" },
    tooltip: { shared: true, intersect: false },
  }), [trend]);

  return (
    <ExecPage
      title="กลุ่มเสี่ยงและการดูแล"
      icon="ri-shield-user-line"
      lead="จำนวนนักเรียนที่ต้องช่วยเหลือ แยกตามแกนและระดับชั้น พร้อมความคืบหน้าการดูแลของครู"
      activeYear={data?.year}
      loading={loading}
      error={error}
      toolbar={<YearPicker years={years} yearId={yearId} onChange={setYearId} activeYear={data?.year} />}
    >
      <Nav variant="pills" className="mb-3 gap-2" activeKey={side} onSelect={(key) => setSide(key || "academic")}>
        <Nav.Item><Nav.Link eventKey="academic">ภาคสามัญ</Nav.Link></Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="islamic" disabled={islamic.available === false}>
            ภาคศาสนา
            {islamic.available === false && <span className="fs-xs ms-1">(ยังไม่มีข้อมูล)</span>}
          </Nav.Link>
        </Nav.Item>
      </Nav>

      {side === "islamic" && (
        <div className="alert alert-light border fs-xs py-2 px-3 mb-3">
          ภาคศาสนามี 2 แกน ไม่มีแกนการเรียน เพราะไม่มีคะแนนเก็บรายชิ้นระหว่างภาค
          มีเพียงคะแนนรวมปลายภาคซึ่งมาช้าเกินกว่าจะเป็นสัญญาณเตือนล่วงหน้า
        </div>
      )}

      <Row className="g-3 mb-4">
        <Col sm={6} xl={3}>
          <StatCard
            icon="ri-alarm-warning-fill" color="danger" label="เสี่ยงสูง"
            value={fmtNum(overall.red)}
            sub={`จากนักเรียน ${fmtNum(active.total)} คน`}
            tone={toNum(overall.red) > 0 ? "danger" : undefined}
          />
        </Col>
        <Col sm={6} xl={3}>
          <StatCard icon="ri-error-warning-fill" color="warning" label="เสี่ยง" value={fmtNum(overall.orange)} sub="ต้องติดตามใกล้ชิด" />
        </Col>
        <Col sm={6} xl={3}>
          <StatCard icon="ri-eye-fill" color="secondary" label="เฝ้าระวัง" value={fmtNum(overall.yellow)} sub="เริ่มมีสัญญาณ" />
        </Col>
        <Col sm={6} xl={3}>
          <StatCard
            icon="ri-heart-pulse-fill" color="info" label="ต้องดูแลทั้งหมด"
            value={fmtNum(active.atRisk)}
            sub={`คิดเป็น ${fmtPct(active.atRiskPercent)} ของนักเรียนทั้งหมด`}
          />
        </Col>
      </Row>

      <Row className="g-3 mb-4">
        <Col xl={7}>
          <Panel title="กระจายตามระดับชั้น" note="เห็นว่าปัญหากระจุกอยู่ที่ชั้นไหน">
            <div className="mb-3"><LevelLegend /></div>
            <RiskHeatmap
              rows={active.byClass}
              labelKey={side === "islamic" ? "ic_id" : "c_id"}
              labelName={side === "islamic" ? "ชั้นศาสนา" : "ระดับชั้น"}
            />
          </Panel>
        </Col>
        <Col xl={5}>
          <Panel title="แยกตามแกนความเสี่ยง" note="นักเรียนหนึ่งคนติดได้หลายแกนพร้อมกัน">
            {Object.keys(active.byAxis || {}).length === 0 ? <EmptyState compact /> : (
              Object.entries(active.byAxis).map(([axis, counts]) => {
                const atRisk = toNum(counts.red) + toNum(counts.orange) + toNum(counts.yellow);
                return (
                  <div key={axis} className="border rounded p-3 mb-2">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <strong>{axisLabels[axis] || axis}</strong>
                      <span className="fw-semibold">{fmtNum(atRisk)} คน</span>
                    </div>
                    <StackedBar
                      segments={LEVEL_ORDER.map((level) => ({ key: level, label: RISK_LABELS[level], value: counts[level], color: RISK_COLORS[level] }))}
                    />
                    <div className="d-flex gap-3 fs-xs text-secondary mt-2">
                      <span style={{ color: RISK_COLORS.red }}>สูง {fmtNum(counts.red)}</span>
                      <span style={{ color: RISK_COLORS.orange }}>เสี่ยง {fmtNum(counts.orange)}</span>
                      <span style={{ color: "#b38600" }}>เฝ้าระวัง {fmtNum(counts.yellow)}</span>
                    </div>
                  </div>
                );
              })
            )}
          </Panel>
        </Col>
      </Row>

      <Row className="g-3 mb-4">
        <Col xs={12}>
          <Panel
            title="แนวโน้มย้อนหลัง"
            note="เก็บยอดไว้วันละครั้ง เพราะย้อนกลับไปคำนวณของเมื่อวานไม่ได้ การเข้าเรียนและคะแนนความประพฤติสะสมไปข้างหน้าอย่างเดียว"
          >
            {!trend.hasTrend ? (
              <div className="text-center text-secondary py-4">
                <i className="ri-line-chart-line d-block mb-2" style={{ fontSize: 28 }} />
                <div className="fs-sm">
                  ระบบเริ่มเก็บข้อมูลแล้ว
                  {trend.points.length === 1 ? " มีข้อมูล 1 วัน" : " แต่ยังไม่มีข้อมูล"}
                  {" "}กราฟจะแสดงเมื่อมีข้อมูลตั้งแต่สองวันขึ้นไป
                </div>
              </div>
            ) : (
              <ReactApexChart
                options={trendOptions}
                series={[
                  { name: RISK_LABELS.red, data: trend.points.map((p) => toNum(p.red)) },
                  { name: RISK_LABELS.orange, data: trend.points.map((p) => toNum(p.orange)) },
                  { name: RISK_LABELS.yellow, data: trend.points.map((p) => toNum(p.yellow)) },
                ]}
                type="area" height={280}
              />
            )}
          </Panel>
        </Col>
      </Row>

      <SectionTitle
        icon="ri-checkbox-multiple-line"
        title="ความคืบหน้าการดูแล"
        note="จากเด็กที่ติดเกณฑ์ กี่คนที่ครูทำครบทุกขั้นตามระดับความเสี่ยงของเด็กคนนั้น"
        color="success"
      />
      <Row className="g-3 mb-4">
        <Col xl={7}>
          <Panel note="เด็กระดับเฝ้าระวังต้องทำเฉพาะขั้นของเฝ้าระวัง ส่วนเด็กเสี่ยงสูงต้องทำครบทุกขั้น">
            <CareProgress perAxis={activeCare.perAxis} axisLabels={axisLabels} />
            {side === "academic" && toNum(academicCare.escalated) > 0 && (
              <div className="border-top pt-3 mt-2 d-flex justify-content-between fs-sm">
                <span className="text-secondary">เคสที่ส่งต่อถึงหัวหน้าระดับแล้ว</span>
                <span className="fw-semibold">{fmtNum(academicCare.escalated)} คน</span>
              </div>
            )}
          </Panel>
        </Col>
        <Col xl={5}>
          <Panel title="เรื่องกังวลที่ยังไม่ปิด" note="ครูแจ้งดูแลแล้วแต่ยังไม่มีการปิดเรื่อง">
            {!concerns.available ? <EmptyState message="ยังไม่มีข้อมูลการแจ้งดูแล" compact /> : (
              <>
                <Row className="g-2 mb-3">
                  <Col xs={6}>
                    <div className="border rounded p-2 text-center">
                      <div className="fs-4 fw-semibold">{fmtNum(concerns.total)}</div>
                      <div className="fs-xs text-secondary">เปิดค้างทั้งหมด</div>
                    </div>
                  </Col>
                  <Col xs={6}>
                    <div className={`border rounded p-2 text-center ${toNum(concerns.overdue) > 0 ? "border-danger" : ""}`}>
                      <div className={`fs-4 fw-semibold ${toNum(concerns.overdue) > 0 ? "text-danger" : ""}`}>{fmtNum(concerns.overdue)}</div>
                      <div className="fs-xs text-secondary">ค้างเกิน 14 วัน</div>
                    </div>
                  </Col>
                </Row>
                {concerns.byCategory.length === 0 ? (
                  <div className="text-secondary fs-sm text-center py-2">ไม่มีเรื่องค้าง</div>
                ) : concerns.byCategory.map((row) => (
                  <div key={row.category} className="d-flex justify-content-between border-bottom py-2 fs-sm">
                    <span>{CONCERN_LABELS[row.category] || row.category}</span>
                    <span>
                      <span className="fw-semibold">{fmtNum(row.total)}</span>
                      {toNum(row.overdue) > 0 && <span className="text-danger fs-xs ms-2">ค้างนาน {fmtNum(row.overdue)}</span>}
                    </span>
                  </div>
                ))}
              </>
            )}
          </Panel>
        </Col>
      </Row>
    </ExecPage>
  );
}
